import React from 'react';
import { 
  Radar, 
  RadarChart, 
  PolarGrid, 
  Legend, 
  PolarAngleAxis, 
  PolarRadiusAxis,
  ResponsiveContainer,
  Tooltip
} from 'recharts';

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="radar-tooltip">
        <p className="tooltip-label">{payload[0].payload.productname}</p>
        <p className="tooltip-value">
          Quantity Sold: {payload[0].value?.toLocaleString()} units
        </p>
      </div>
    );
  }
  return null;
};

function ProductChart(props) {
  const { data } = props;

  if (!data || data.length === 0) {
    return (
      <div className="chart-empty-state">
        <p>No product data available</p>
      </div>
    );
  }

  return (
    <div className="product-chart-container">
      <div className="chart-header">
        <h3>Product Performance Overview</h3>
        <p className="chart-subtitle">Quantity sold by product</p>
      </div>
      <div className="chart-wrapper">
        <ResponsiveContainer width="100%" height={400}>
          <RadarChart 
            cx="50%" 
            cy="50%" 
            outerRadius="70%" 
            data={data}
          >
            <PolarGrid 
              stroke="#e0e0e0"
              strokeWidth={1}
            />
            <PolarAngleAxis 
              dataKey="productname" 
              tick={{ 
                fontSize: 11, 
                fill: '#666',
                fontWeight: 500
              }}
              tickLine={{ stroke: '#ccc' }}
            />
            <PolarRadiusAxis 
              angle={30} 
              domain={[0, 'auto']}
              tick={{ fontSize: 10, fill: '#999' }}
              axisLine={{ stroke: '#ccc' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ paddingTop: 15 }}
              iconType="circle"
              formatter={(value) => <span style={{ color: '#333', fontSize: 12 }}>{value}</span>}
            />
            <Radar 
              name="Quantity Sold" 
              dataKey="totalqty" 
              stroke="#8884d8"
              strokeWidth={2.5}
              fill="#8884d8" 
              fillOpacity={0.6}
              animationDuration={1500}
              animationEasing="ease-in-out"
              dot={{ 
                r: 4, 
                fill: "#8884d8",
                strokeWidth: 2,
                stroke: "#fff"
              }}
            />
          </RadarChart>
        </ResponsiveContainer>
      </div>
      <div className="chart-footer">
        <div className="stats-summary">
          <div className="stat-item">
            <span className="stat-label">Total Products:</span>
            <span className="stat-value">{data.length}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Total Units Sold:</span>
            <span className="stat-value">
              {data.reduce((sum, item) => sum + (item.totalqty || 0), 0).toLocaleString()}
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Avg per Product:</span>
            <span className="stat-value">
              {Math.round(data.reduce((sum, item) => sum + (item.totalqty || 0), 0) / data.length).toLocaleString()}
            </span>
          </div>
        </div>
      </div>

      <style>{`
        .product-chart-container {
          background: white;
          border-radius: 16px;
          padding: 20px;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
        }
        
        .product-chart-container:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
        }
        
        .chart-header {
          margin-bottom: 20px;
          padding-bottom: 15px;
          border-bottom: 2px solid #f0f0f0;
        }
        
        .chart-header h3 {
          margin: 0 0 5px 0;
          font-size: 16px;
          font-weight: 600;
          color: #333;
        }
        
        .chart-subtitle {
          margin: 0;
          font-size: 12px;
          color: #999;
        }
        
        .chart-wrapper {
          width: 100%;
          min-height: 400px;
        }
        
        .chart-footer {
          margin-top: 20px;
          padding-top: 15px;
          border-top: 1px solid #f0f0f0;
        }
        
        .stats-summary {
          display: flex;
          justify-content: space-around;
          flex-wrap: wrap;
          gap: 20px;
        }
        
        .stat-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 5px;
        }
        
        .stat-label {
          font-size: 11px;
          color: #999;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .stat-value {
          font-size: 18px;
          font-weight: 700;
          color: #8884d8;
        }
        
        .chart-empty-state {
          background: white;
          border-radius: 16px;
          padding: 60px 20px;
          text-align: center;
          color: #999;
          font-size: 14px;
        }
        
        /* Global Tooltip Styles */
        :global(.radar-tooltip) {
          background: rgba(0, 0, 0, 0.85);
          padding: 10px 15px;
          border-radius: 8px;
          color: white;
          min-width: 160px;
        }
        
        :global(.tooltip-label) {
          margin: 0 0 5px 0;
          font-size: 12px;
          font-weight: 600;
          color: #ffd700;
        }
        
        :global(.tooltip-value) {
          margin: 0;
          font-size: 13px;
          font-weight: 500;
        }
        
        /* Responsive */
        @media (max-width: 768px) {
          .product-chart-container {
            padding: 15px;
          }
          
          .chart-header h3 {
            font-size: 14px;
          }
          
          .stats-summary {
            flex-direction: column;
            align-items: center;
            gap: 12px;
          }
          
          .stat-value {
            font-size: 16px;
          }
        }
        
        @media (max-width: 480px) {
          .chart-wrapper {
            min-height: 350px;
          }
          
          :global(.recharts-polar-angle-axis-tick-value) {
            font-size: 9px !important;
          }
        }
      `}</style>
    </div>
  );
}

export default ProductChart;